import RSelect from 'react-select';

import { observer } from 'mobx-react-lite';
import styled from 'styled-components';

import { FormLabel } from '@components/form-label';
import { IconCloseSmall } from '@components/icons/close-small';
import { Stack } from '@components/stack';

import { useStore } from 'models/Provider';

export const InlineMultiSelect = observer((props) => {
  const { theme, t } = useStore();
  const styles = {
    menu: (provided) => ({
      ...provided,
      backgroundColor: theme.colors.white100,
      borderRadius: '0.25rem',
      margin: 0,
      padding: '1rem',
      boxShadow: '0px 0.5rem 1.5rem rgba(0, 0, 0, 0.16)',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: 'transparent',
      font: theme.text.regular14,
      color: theme.colors.purple100,
      minHeight: '2.5rem',
      display: 'flex',
      alignItems: 'center',
      borderRadius: '0.25rem',
      padding: '0 0.75rem',
      ...(state.isFocused && {
        backgroundColor: theme.colors.purpleTrans5,
      }),
      ':hover': {
        cursor: 'pointer',
      },
      ':active': {},
    }),
    valueContainer: (provided) => ({
      ...provided,
      paddingTop: 0,
      paddingBottom: 0,
      cursor: 'pointer',
    }),
    singleValue: (provided) => ({
      ...provided,
      color: theme.colors.purple100,
    }),
    control: (provided, state) => ({
      ...provided,
      boxShadow: 'none',
      font: theme.text.regular16,
      transitionProperty: 'border-color, background-color',
      transitionTimingFunction: 'ease-out',
      transitionDuration: '200ms',
      borderWidth: '0.0625rem',
      borderStyle: 'solid',
      borderColor: state.isFocused ? theme.colors.purple100 : 'transparent',
      backgroundColor: theme.colors.purpleTrans5,
      borderRadius: '0.25rem',
      minHeight: '2.5rem',
      display: 'flex',
      alignItems: 'center',
      padding: 0,
      outline: 'none',
      '&:hover': {},
      '&:active': {},
      '&:focus': {},
      ...(!state.isFocused && {
        ':hover': {
          borderColor: theme.colors.purple100,
          backgroundColor: theme.colors.purpleTrans10,
        },
      }),
    }),
    placeholder: (provided) => ({
      ...provided,
      color: theme.colors.purple50,
    }),
  };
  return (
    <Stack space={1}>
      <FormLabel text={props.label} />
      <RSelect
        onChange={props.onChange}
        menuPlacement="auto"
        openMenuOnFocus
        menuPortalTarget={document.body}
        isMulti={true}
        closeMenuOnSelect={false}
        styles={styles}
        isClearable={false}
        placeholder={props.placeholder}
        defaultValue={props.defaultValue}
        noOptionsMessage={() => t('multiselect.nooptions')}
        options={props.options}
        components={{
          DropdownIndicator: null,
          ClearIndicator: null,
          MultiValue,
          Input: CustomInput,
        }}
      />
    </Stack>
  );
});

function CustomInput(props) {
  const { innerRef, cx, getStyles, theme, isHidden, isDisabled, value, ...rest } = props;
  return (
    <InputBox
      ref={innerRef}
      style={{ width: _calcTW(value, "normal 400 16px 'Work Sans'") }}
      value={value}
      {...rest}
    />
  );
}

let InputBox = styled.input((p) => ({
  border: 'none',
  backgroundColor: 'transparent',
  font: p.theme.text.regular16,
  color: p.theme.colors.purple100,
  outline: 'none',
}));

function MultiValue({ children, removeProps, ...rest }) {
  return (
    <StyledMultiValue {...removeProps}>
      <span>{children}</span>
      <IconCloseSmall />
    </StyledMultiValue>
  );
}

let StyledMultiValue = styled.div((p) => ({
  display: 'flex',
  alignItems: 'center',
  border: `0.0625rem solid ${p.theme.colors.blue100}`,
  margin: '0.25rem 0.375rem 0.25rem 0',
  borderRadius: '0.25rem',
  padding: '0.25rem 0.25rem 0.25rem 0.5rem',
  backgroundColor: p.theme.colors.blue10,
  color: p.theme.colors.blue100,
  font: p.theme.text.semibold12,
  ':hover': {
    backgroundColor: p.theme.colors.blue20,
    color: p.theme.colors.blue200,
    cursor: 'pointer',
  },
  span: {
    display: 'block',
    marginRight: '0.25rem',
  },
}));

const _calcTW = (value, font) => {
  const canvas = _calcTW.canvas || (_calcTW.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  context.font = font;
  const metrics = context.measureText(value);
  return metrics.width + 10;
};
